import React, { useEffect, useState, useRef } from "react";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";
import { HiMail, HiExclamation, HiX, HiCheck } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";

interface Recipient {
  user_id: number;
  email: string;
  name: string;
  email_notifications_enabled: boolean;
  is_owner?: boolean;
}

interface SendSummaryEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId: number;
  programTitle: string;
  runId?: number;
  runTitle?: string;
  onSuccess?: () => void;
}

interface SendHistoryItem {
  id: number;
  sender: string;
  action: string;
  created_at: string;
}

const SendSummaryEmailModal: React.FC<SendSummaryEmailModalProps> = ({
  isOpen,
  onClose,
  programId,
  programTitle,
  runId,
  runTitle,
  onSuccess
}) => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [ccList, setCcList] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [history, setHistory] = useState<SendHistoryItem[]>([]);
  const [formatOption, setFormatOption] = useState<"normal" | "branded">("branded");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    to: string[];
    cc: string[];
    subject: string;
    body: string;
  } | null>(null);

  const [editedSubject, setEditedSubject] = useState<string>("");
  const [editedBody, setEditedBody] = useState<string>("");
  const editorRef = useRef<HTMLDivElement>(null);

  // Fetch recipients list
  useEffect(() => {
    if (!isOpen) return;

    const fetchRecipients = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/v2/programs/${programId}/summary-email-recipients${runId ? `?run_id=${runId}` : ""}`);
        if (res.data) {
          const list = res.data.recipients || [];
          setRecipients(list);
          setCcList(res.data.cc || []);
          setHistory(res.data.history || []);
          
          // Pre-select only the business owner (owner of the program)
          const owner = list.find((r: Recipient) => r.is_owner);
          if (owner && owner.email_notifications_enabled) {
            setSelectedIds([owner.user_id]);
          } else {
            setSelectedIds([]);
          }
        }
      } catch (err: any) {
        console.error("Failed to fetch recipients:", err);
        toast.error("Failed to load recipients list");
      } finally {
        setLoading(false);
      }
    };

    fetchRecipients();
  }, [isOpen, programId, runId]);

  // Fetch preview when recipients, ccList, or run changes
  useEffect(() => {
    if (!isOpen || loading) return;

    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(`/v2/programs/${programId}/summary-email-preview`, {
          recipients: selectedIds,
          run_id: runId,
          cc: ccList
        });
        if (res.data) {
          setPreviewData(res.data);
          setEditedSubject(res.data.subject);
          setEditedBody(res.data.body);
        }
      } catch (err: any) {
        console.error("Failed to fetch preview:", err);
        toast.error("Failed to load email preview");
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [selectedIds, ccList, runId, programId, isOpen, loading]);

  const handleToggleSelect = (userId: number) => {
    setSelectedIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const enabledUsers = recipients.filter(r => r.email_notifications_enabled);
    if (selectedIds.length === enabledUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(enabledUsers.map(r => r.user_id));
    }
  };

  const handleRemoveCc = (emailToRemove: string) => {
    setCcList(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleAddCc = () => {
    const trimmed = ccInput.trim();
    if (trimmed && !ccList.includes(trimmed)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmed)) {
        setCcList(prev => [...prev, trimmed]);
        setCcInput("");
      } else {
        toast.error("Please enter a valid email address");
      }
    }
  };

  const handleCcKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddCc();
    }
  };

  const handleFormatChange = (format: "normal" | "branded") => {
    if (editorRef.current) {
      setEditedBody(editorRef.current.innerHTML);
    }
    setFormatOption(format);
  };

  const handleSend = () => {
    // Capture body before modal unmounts
    const bodyToSend = editorRef.current ? editorRef.current.innerHTML : editedBody;
    const payload = {
      recipients: selectedIds,
      run_id: runId,
      subject: editedSubject,
      body: bodyToSend,
      cc: ccList,
      format: formatOption
    };

    // 1. Show success state immediately
    if (selectedIds.length === 0) {
      toast("Sending summary email exclusively to CC list...", { icon: "ℹ️" });
    }
    setSent(true);
    if (onSuccess) onSuccess();

    // 2. Close the modal after a short visual delay
    setTimeout(() => {
      onClose();
      setSent(false);
    }, 1500);

    // 3. Fire the actual API call in the background
    axios.post(`/v2/programs/${programId}/summary-email-send`, payload).catch((err: any) => {
      console.error("Failed to send summary email:", err);
      toast.error(err.response?.data?.message || "Email dispatch failed — please try again.");
    });
  };

  if (!isOpen) return null;

  const enabledRecipients = recipients.filter(r => r.email_notifications_enabled);
  const noSelectableRecipients = enabledRecipients.length === 0;

  // ─── Optimistic success screen ────────────────────────────────────────────
  if (sent) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="bg-white rounded-2xl shadow-2xl px-12 py-14 flex flex-col items-center gap-5 max-w-sm w-full border border-gray-100"
          >
            {/* Animated check circle */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.05 }}
              className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
              >
                <HiCheck className="w-10 h-10 text-green-600" />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <h3 className="text-xl font-bold text-gray-900">Email Sent!</h3>
              <p className="text-sm text-gray-500 mt-1.5">
                Summary email dispatched to{" "}
                <span className="font-semibold text-gray-700">
                  {selectedIds.length > 0 ? `${selectedIds.length} recipient${selectedIds.length > 1 ? "s" : ""}` : "CC list"}
                </span>
                {ccList.length > 0 && selectedIds.length > 0 && (
                  <> + {ccList.length} CC</>  
                )}
              </p>
              <p className="text-xs text-gray-400 mt-3">Closing in a moment…</p>
            </motion.div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  return (
    <>
      <style>{`
        .email-editor-content strong, .email-editor-content b {
          font-weight: 800 !important;
          color: #111827 !important;
        }
        .email-editor-content em, .email-editor-content i {
          font-style: italic !important;
        }
        .email-editor-content ul {
          list-style-type: disc !important;
          padding-left: 20px !important;
          margin-bottom: 16px !important;
        }
        .email-editor-content li {
          display: list-item !important;
          margin-bottom: 6px !important;
        }
      `}</style>
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col my-4 max-h-[95vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 text-blue-600">
                <HiMail className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Send Pentest Summary Email</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {programTitle} {runTitle ? `(${runTitle})` : ""}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-150 rounded-lg"
            >
              <HiX className="w-6 h-6" />
            </button>
          </div>

          {/* Body content */}
          <div className="flex-1 overflow-y-auto p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-0">
            {/* Left Column: Recipients Selection */}
            <div className="lg:col-span-5 flex flex-col space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2.5">Select Recipients (To)</h4>
                
                {loading && recipients.length === 0 ? (
                  <div className="py-8 text-center text-gray-500 text-sm">Loading users...</div>
                ) : recipients.length === 0 ? (
                  <div className="py-8 text-center text-gray-500 text-sm">No client users found for this program.</div>
                ) : (
                  <div className="border border-gray-200 rounded-xl divide-y divide-gray-200 overflow-hidden bg-white max-h-[320px] overflow-y-auto shadow-sm">
                    {/* Header Select All */}
                    {!noSelectableRecipients && (
                      <div 
                        onClick={handleSelectAll}
                        className="flex items-center px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors text-xs font-semibold text-gray-600"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.length === enabledRecipients.length && enabledRecipients.length > 0}
                          onChange={() => {}}
                          className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Select All Opted-In Users ({enabledRecipients.length})
                      </div>
                    )}

                    {/* Recipients List */}
                    {recipients.map(recipient => {
                      const isSelected = selectedIds.includes(recipient.user_id);
                      const isOptedOut = !recipient.email_notifications_enabled;

                      return (
                        <div
                          key={recipient.user_id}
                          onClick={() => !isOptedOut && handleToggleSelect(recipient.user_id)}
                          className={`flex items-center px-4 py-3 transition-colors ${
                            isOptedOut
                              ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                              : "hover:bg-blue-50/40 cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isOptedOut}
                            onChange={() => {}}
                            className={`mr-3 rounded border-gray-300 focus:ring-blue-500 ${
                              isOptedOut ? "text-gray-300 cursor-not-allowed" : "text-blue-600"
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate">
                              {recipient.name}
                            </p>
                            <p className="text-xs truncate text-gray-500">
                              {recipient.email}
                            </p>
                          </div>
                          {isOptedOut && (
                            <span className="text-[10px] font-medium bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full uppercase tracking-wider ml-2">
                              notifications off
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Email Template Format Selector */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Email Design Template</h4>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleFormatChange("normal")}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                      formatOption === "normal"
                        ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm font-bold"
                        : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <span>Normal Style</span>
                    <span className="text-[10px] text-gray-500 font-normal mt-0.5">Plain HTML / Editable</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatChange("branded")}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                      formatOption === "branded"
                        ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm font-bold"
                        : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <span>CTB Branded Style</span>
                    <span className="text-[10px] text-gray-500 font-normal mt-0.5">With logo & theme</span>
                  </button>
                </div>
              </div>

              {/* CC info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">CC Recipients (Editable)</h4>
                <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-3 shadow-sm">
                  {/* Tag List */}
                  <div className="flex flex-wrap gap-2">
                    {ccList.map(email => (
                      <span 
                        key={email}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-150 text-gray-800 border border-gray-300"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveCc(email)}
                          className="text-gray-500 hover:text-red-650 transition-colors focus:outline-none"
                        >
                          <HiX className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                    {ccList.length === 0 && (
                      <span className="text-xs text-gray-400 italic">No CC recipients added</span>
                    )}
                  </div>

                  {/* Add Input */}
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Add CC email..."
                      value={ccInput}
                      onChange={(e) => setCcInput(e.target.value)}
                      onKeyDown={handleCcKeyDown}
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddCc}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-xs font-semibold transition-colors focus:outline-none"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Warning Banner */}
              {(noSelectableRecipients || selectedIds.length === 0) && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex items-start gap-3">
                  <HiExclamation className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-semibold">CC Only Send</h5>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {noSelectableRecipients 
                        ? "No client users have email notifications enabled. You are still permitted to send the summary email exclusively to the CC list."
                        : "No recipients selected in the 'To' field. The summary email will be sent exclusively to the CC list."
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Sending History */}
              <div className="pt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Recent Send History</h4>
                <div className="border border-gray-200 rounded-xl bg-gray-50 p-4 space-y-3 max-h-[140px] overflow-y-auto shadow-sm">
                  {history.length > 0 ? (
                    history.map(item => (
                      <div key={item.id} className="text-xs border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
                        <div className="flex items-center justify-between text-gray-500 font-medium">
                          <span>By: {item.sender}</span>
                          <span>{new Date(item.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-700 mt-1 font-semibold break-words">
                          {item.action}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      No previous dispatches recorded.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Live Email Preview */}
            <div className="lg:col-span-7 flex flex-col border border-gray-200 rounded-xl bg-gray-50 overflow-hidden min-h-[480px] shadow-sm">
              <div className="px-5 py-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Email Live Preview</span>
                {previewLoading && (
                  <span className="text-xs text-blue-600 animate-pulse">Loading preview...</span>
                )}
              </div>

              {previewData ? (
                <div className="p-6 flex-1 flex flex-col space-y-4 overflow-y-auto max-h-[520px] text-xs">
                  {/* To row */}
                  <div className="flex items-start">
                    <span className="w-16 font-bold text-gray-500">To:</span>
                    <span className="flex-1 break-all text-gray-800 font-semibold">
                      {previewData.to.length > 0 ? previewData.to.join(", ") : "(CC Only)"}
                    </span>
                  </div>

                  {/* CC row */}
                  <div className="flex items-start">
                    <span className="w-16 font-bold text-gray-500">CC:</span>
                    <span className="flex-1 break-all text-gray-800 font-semibold">
                      {previewData.cc.join(", ")}
                    </span>
                  </div>

                  {/* Subject row */}
                  <div className="flex items-center border-b border-gray-200 pb-3">
                    <span className="w-16 font-bold text-gray-500">Subject:</span>
                    <input
                      type="text"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      className="flex-1 px-3 py-1.5 border border-gray-350 rounded-lg text-xs font-semibold text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm"
                    />
                  </div>

                  {/* Email Body Editor */}
                  {formatOption === "branded" ? (
                    <div className="bg-[#F0F2F5] p-6 rounded-xl border border-gray-200 overflow-y-auto max-h-[400px]">
                      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm max-w-[500px] mx-auto">
                        {/* Static Header */}
                        <div className="text-center pt-8 pb-4 border-b border-gray-100 bg-white">
                          <img 
                            src="https://c8270c56-7594-4100-9b34-7589297588fa.b-cdn.net/e/bac89f4c-a951-463b-81dd-86c0e12c3427/28695d5f-3f29-41fa-a53d-2f93062026e6.jpeg" 
                            alt="Capture The Bug" 
                            className="h-10 mx-auto" 
                          />
                          <h1 className="text-md font-bold text-gray-900 mt-3">Pentest Summary Report</h1>
                        </div>
                        
                        <div className="p-5 bg-white">
                          <div 
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning
                            className="email-editor-content bg-white border border-dashed border-blue-200 hover:border-blue-400 rounded-xl p-5 prose prose-sm max-w-none text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[260px] transition-all"
                            dangerouslySetInnerHTML={{ __html: editedBody }}
                          />
                        </div>

                        {/* Static Footer */}
                        <div className="text-center pb-8 pt-4 text-[10px] text-gray-400 bg-white border-t border-gray-100">
                          <p>© {new Date().getFullYear()} Capture The Bug. All rights reserved.</p>
                          <p className="mt-1">LinkedIn • Twitter • Support</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="email-editor-content flex-1 bg-white border border-gray-200 rounded-lg p-5 overflow-y-auto prose prose-sm max-w-none text-gray-800 shadow-inner outline-none focus:ring-1 focus:ring-blue-500 min-h-[300px]"
                      dangerouslySetInnerHTML={{ __html: editedBody }}
                    />
                  )}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-400 text-sm">
                  {previewLoading ? "Generating live preview..." : "Please select recipients to render preview."}
                </div>
              )}
            </div>
          </div>

          {/* Footer Controls */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-150 transition-colors text-sm font-semibold rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={selectedIds.length === 0 && ccList.length === 0}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white transition-colors text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <HiCheck className="w-4 h-4" />
              Send Summary Email
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  </>
  );
};

export default SendSummaryEmailModal;
